import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Bell,
  Check,
  Gift,
  ListChecks,
  Pencil,
  Plus,
  Save,
  Settings2,
  SlidersHorizontal,
  UsersRound,
  X
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, ApiClientError } from '../api';
import type { ChildSettings, Reward, Task } from '../types';
import { ChildProfilesPanel } from '../components/ChildProfilesPanel';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';

const categories = [
  ['toy', 'Đồ chơi'],
  ['experience', 'Trải nghiệm'],
  ['book', 'Sách'],
  ['privilege', 'Quyền lựa chọn'],
  ['family_time', 'Thời gian gia đình'],
  ['food', 'Ẩm thực'],
  ['saving', 'Mục tiêu tiết kiệm']
] as const;

type ManageTab = 'children' | 'tasks' | 'rewards' | 'rules';

function number(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}

function validTab(value: string | null): value is ManageTab {
  return value === 'children' || value === 'tasks' || value === 'rewards' || value === 'rules';
}

export function ManagePage() {
  const { selectedChild, refresh } = useAuth();
  const { show } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTabState] = useState<ManageTab>(validTab(requestedTab) ? requestedTab : 'children');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [settings, setSettings] = useState<ChildSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', points: 3, icon: '⭐', category: 'habit', reminderTime: ''
  });
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [rewardForm, setRewardForm] = useState({
    title: '', description: '', category: 'experience', pointCost: 300, priceVnd: 0,
    icon: '🎁', tags: '', cooldownDays: 0
  });

  function setTab(value: ManageTab) {
    setTabState(value);
    setSearchParams(value === 'children' ? {} : { tab: value }, { replace: true });
  }

  const load = useCallback(async () => {
    if (!selectedChild) return;
    setLoading(true);
    try {
      const [taskRows, rewardRows, childRules] = await Promise.all([
        api<Task[]>('/tasks'),
        api<Reward[]>('/rewards'),
        api<ChildSettings>('/child/settings')
      ]);
      setTasks(taskRows);
      setRewards(rewardRows);
      setSettings(childRules);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể tải phần quản lý.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedChild?.id, show]);

  useEffect(() => { void load(); }, [load]);

  async function addTask(event: FormEvent) {
    event.preventDefault();
    if (taskForm.points === 0) {
      show('Số điểm phải khác 0.', 'error');
      return;
    }
    setBusy('task:add');
    try {
      await api('/tasks', {
        method: 'POST',
        body: {
          ...taskForm,
          scheduleDays: [1, 2, 3, 4, 5, 6, 7],
          reminderTime: taskForm.reminderTime || null,
          active: true
        }
      });
      setTaskForm({ title: '', description: '', points: 3, icon: '⭐', category: 'habit', reminderTime: '' });
      show(`Đã thêm nhiệm vụ riêng cho ${selectedChild?.nickname}.`, 'success');
      await load();
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể thêm nhiệm vụ.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function toggleTask(task: Task) {
    setBusy(`task:${task.id}`);
    try {
      await api(`/tasks/${encodeURIComponent(task.id)}`, { method: 'PATCH', body: { active: !task.active } });
      await load();
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể cập nhật nhiệm vụ.', 'error');
    } finally {
      setBusy('');
    }
  }

  function startEditTask(task: Task) {
    setEditingTask({ ...task });
  }

  async function saveEditedTask(event: FormEvent) {
    event.preventDefault();
    if (!editingTask) return;
    if (editingTask.points === 0) {
      show('Số điểm phải khác 0.', 'error');
      return;
    }
    setBusy(`task:edit:${editingTask.id}`);
    try {
      await api(`/tasks/${encodeURIComponent(editingTask.id)}`, {
        method: 'PATCH',
        body: {
          title: editingTask.title,
          description: editingTask.description,
          points: editingTask.points,
          icon: editingTask.icon,
          category: editingTask.category,
          scheduleDays: editingTask.scheduleDays,
          reminderTime: editingTask.reminderTime,
          active: editingTask.active
        }
      });
      show(`Đã cập nhật nhiệm vụ “${editingTask.title}”.`, 'success');
      setEditingTask(null);
      await load();
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể sửa nhiệm vụ.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function addReward(event: FormEvent) {
    event.preventDefault();
    setBusy('reward:add');
    try {
      await api('/rewards', {
        method: 'POST',
        body: {
          ...rewardForm,
          tags: rewardForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
          active: true
        }
      });
      setRewardForm({
        title: '', description: '', category: 'experience', pointCost: 300,
        priceVnd: 0, icon: '🎁', tags: '', cooldownDays: 0
      });
      show(`Đã thêm quà vào catalog riêng của ${selectedChild?.nickname}.`, 'success');
      await load();
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể thêm quà.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function toggleReward(reward: Reward) {
    setBusy(`reward:${reward.id}`);
    try {
      await api(`/rewards/${encodeURIComponent(reward.id)}`, {
        method: 'PATCH',
        body: { active: !reward.active }
      });
      await load();
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể cập nhật quà.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function saveSettings(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setBusy('settings');
    try {
      const updated = await api<ChildSettings>('/child/settings', {
        method: 'PATCH',
        body: settings
      });
      setSettings(updated);
      await refresh();
      show(`Đã lưu quy tắc riêng của ${selectedChild?.nickname}.`, 'success');
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể lưu quy tắc.', 'error');
    } finally {
      setBusy('');
    }
  }

  if (!selectedChild) return <Loading label="Đang mở hồ sơ…" />;
  if (loading && tab !== 'children') return <Loading label="Đang mở phần quản lý…" />;

  return (
    <div className="page manage-page">
      <div className="page-title">
        <span className="eyebrow"><Settings2 size={15} /> Dành cho phụ huynh</span>
        <h1>Quản lý hồ sơ bé</h1>
        <p>Mỗi hồ sơ vận hành độc lập: nhiệm vụ, điểm, quà, ngân sách, sở thích, lịch sử và gợi ý AI không dùng lẫn nhau.</p>
      </div>

      <div className="selected-child-scope">
        <span aria-hidden="true">👦</span>
        <div><small>Đang cấu hình cho</small><strong>{selectedChild.nickname}</strong></div>
        <span className="scope-lock">Dữ liệu riêng</span>
      </div>

      <div className="segmented-tabs segmented-tabs--four" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'children'} className={tab === 'children' ? 'active' : ''} onClick={() => setTab('children')}><UsersRound size={18} /> Hồ sơ bé</button>
        <button type="button" role="tab" aria-selected={tab === 'tasks'} className={tab === 'tasks' ? 'active' : ''} onClick={() => setTab('tasks')}><ListChecks size={18} /> Nhiệm vụ</button>
        <button type="button" role="tab" aria-selected={tab === 'rewards'} className={tab === 'rewards' ? 'active' : ''} onClick={() => setTab('rewards')}><Gift size={18} /> Catalog quà</button>
        <button type="button" role="tab" aria-selected={tab === 'rules'} className={tab === 'rules' ? 'active' : ''} onClick={() => setTab('rules')}><SlidersHorizontal size={18} /> Quy tắc</button>
      </div>

      {tab === 'children' && <ChildProfilesPanel />}

      {tab === 'tasks' && (
        <div className="manage-stack">
          <form className="surface manage-form" onSubmit={addTask}>
            <div className="section-heading"><div><span className="section-icon section-icon--blue"><Plus size={19} /></span><h2>Thêm nhiệm vụ cho {selectedChild.nickname}</h2></div></div>
            <p className="form-help">Điểm dương để ghi nhận; điểm âm để trừ khi vi phạm quy tắc. Không dùng mức 0 điểm.</p>
            <div className="form-grid form-grid--compact">
              <label>Tên nhiệm vụ<input required value={taskForm.title} onChange={(event) => setTaskForm({ ...taskForm, title: event.target.value })} placeholder="Ví dụ: Tưới cây hoặc Nói dối" /></label>
              <label>Số điểm (+/−)<input required type="number" min="-100" max="100" value={taskForm.points} onChange={(event) => setTaskForm({ ...taskForm, points: Number(event.target.value) })} /></label>
              <label>Biểu tượng<input required value={taskForm.icon} maxLength={12} onChange={(event) => setTaskForm({ ...taskForm, icon: event.target.value })} /></label>
              <label>Nhóm<select value={taskForm.category} onChange={(event) => setTaskForm({ ...taskForm, category: event.target.value })}><option value="habit">Thói quen</option><option value="learning">Học tập</option><option value="independence">Tự lập</option><option value="family">Gia đình</option><option value="penalty">Vi phạm / trừ điểm</option></select></label>
              <label>Giờ nhắc<input type="time" value={taskForm.reminderTime} onChange={(event) => setTaskForm({ ...taskForm, reminderTime: event.target.value })} /></label>
            </div>
            <label>Mô tả<textarea rows={2} maxLength={500} value={taskForm.description} onChange={(event) => setTaskForm({ ...taskForm, description: event.target.value })} placeholder="Mô tả rõ khi nào áp dụng điểm." /></label>
            <button className="button button--primary" disabled={busy === 'task:add'} type="submit"><Plus size={17} /> Thêm nhiệm vụ</button>
          </form>

          <section className="surface">
            <div className="section-heading"><div><span className="section-icon section-icon--orange"><ListChecks size={19} /></span><h2>Nhiệm vụ của {selectedChild.nickname}</h2></div><span>{tasks.filter((item) => item.active).length} đang dùng</span></div>
            <div className="manage-list">
              {tasks.length === 0 && <p className="empty-copy">Hồ sơ này chưa có nhiệm vụ.</p>}
              {tasks.map((task) => editingTask?.id === task.id && editingTask ? (
                <form key={task.id} className="task-edit-card" onSubmit={saveEditedTask}>
                  <div className="task-edit-card__heading"><Pencil size={18} /><strong>Sửa nhiệm vụ</strong></div>
                  <div className="form-grid form-grid--compact">
                    <label>Tên nhiệm vụ<input required value={editingTask.title} onChange={(event) => setEditingTask({ ...editingTask, title: event.target.value })} /></label>
                    <label>Số điểm (+/−)<input required type="number" min="-100" max="100" value={editingTask.points} onChange={(event) => setEditingTask({ ...editingTask, points: Number(event.target.value) })} /></label>
                    <label>Biểu tượng<input required maxLength={12} value={editingTask.icon} onChange={(event) => setEditingTask({ ...editingTask, icon: event.target.value })} /></label>
                    <label>Nhóm<select value={editingTask.category} onChange={(event) => setEditingTask({ ...editingTask, category: event.target.value })}><option value="habit">Thói quen</option><option value="learning">Học tập</option><option value="independence">Tự lập</option><option value="family">Gia đình</option><option value="penalty">Vi phạm / trừ điểm</option></select></label>
                    <label>Giờ nhắc<input type="time" value={editingTask.reminderTime ?? ''} onChange={(event) => setEditingTask({ ...editingTask, reminderTime: event.target.value || null })} /></label>
                  </div>
                  <label>Mô tả<textarea rows={2} maxLength={500} value={editingTask.description} onChange={(event) => setEditingTask({ ...editingTask, description: event.target.value })} /></label>
                  <div className="inline-actions">
                    <button type="submit" className="button button--primary button--small" disabled={Boolean(busy)}><Check size={16} /> Lưu thay đổi</button>
                    <button type="button" className="button button--soft button--small" onClick={() => setEditingTask(null)}><X size={16} /> Hủy</button>
                  </div>
                </form>
              ) : (
                <div key={task.id} className={`manage-row task-manage-row ${!task.active ? 'muted' : ''} ${task.points < 0 ? 'is-penalty' : ''}`}>
                  <span className="manage-emoji">{task.icon}</span>
                  <div><strong>{task.title}</strong><small>{task.points > 0 ? '+' : ''}{task.points} điểm{task.reminderTime ? ` · nhắc ${task.reminderTime}` : ''}</small></div>
                  <div className="task-row-actions">
                    <button type="button" className="icon-action" aria-label={`Sửa ${task.title}`} title="Sửa nhiệm vụ" disabled={Boolean(busy)} onClick={() => startEditTask(task)}><Pencil size={17} /></button>
                    <label className="switch"><input type="checkbox" checked={task.active} disabled={Boolean(busy)} onChange={() => void toggleTask(task)} /><span /></label>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'rewards' && (
        <div className="manage-stack">
          <form className="surface manage-form" onSubmit={addReward}>
            <div className="section-heading"><div><span className="section-icon section-icon--purple"><Plus size={19} /></span><h2>Thêm quà cho {selectedChild.nickname}</h2></div></div>
            <label>Tên quà<input required value={rewardForm.title} onChange={(event) => setRewardForm({ ...rewardForm, title: event.target.value })} placeholder="Ví dụ: Đi nhà sách cuối tuần" /></label>
            <label>Mô tả<textarea rows={2} value={rewardForm.description} onChange={(event) => setRewardForm({ ...rewardForm, description: event.target.value })} /></label>
            <div className="form-grid form-grid--compact">
              <label>Loại quà<select value={rewardForm.category} onChange={(event) => setRewardForm({ ...rewardForm, category: event.target.value })}>{categories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>Điểm đổi<input required type="number" min="1" value={rewardForm.pointCost} onChange={(event) => setRewardForm({ ...rewardForm, pointCost: Number(event.target.value) })} /></label>
              <label>Giá dự kiến (đ)<input required type="number" min="0" step="10000" value={rewardForm.priceVnd} onChange={(event) => setRewardForm({ ...rewardForm, priceVnd: Number(event.target.value) })} /></label>
              <label>Cooldown (ngày)<input type="number" min="0" value={rewardForm.cooldownDays} onChange={(event) => setRewardForm({ ...rewardForm, cooldownDays: Number(event.target.value) })} /></label>
              <label>Biểu tượng<input required value={rewardForm.icon} maxLength={12} onChange={(event) => setRewardForm({ ...rewardForm, icon: event.target.value })} /></label>
              <label>Tags (phân cách dấu phẩy)<input value={rewardForm.tags} onChange={(event) => setRewardForm({ ...rewardForm, tags: event.target.value })} placeholder="science, discovery" /></label>
            </div>
            <button className="button button--primary" disabled={busy === 'reward:add'} type="submit"><Plus size={17} /> Thêm vào catalog</button>
          </form>

          <section className="surface">
            <div className="section-heading"><div><span className="section-icon section-icon--purple"><Gift size={19} /></span><h2>Catalog quà của {selectedChild.nickname}</h2></div><span>{rewards.filter((item) => item.active).length} đang bật</span></div>
            <div className="manage-list">
              {rewards.length === 0 && <p className="empty-copy">Hồ sơ này chưa có quà được phép.</p>}
              {rewards.map((reward) => (
                <div key={reward.id} className={`manage-row ${!reward.active ? 'muted' : ''}`}>
                  <span className="manage-emoji">{reward.icon}</span>
                  <div><strong>{reward.title}</strong><small>{number(reward.pointCost)} điểm · {reward.priceVnd ? `${number(reward.priceVnd)}đ` : '0đ'} · {reward.cooldownDays} ngày</small></div>
                  <label className="switch"><input type="checkbox" checked={Boolean(reward.active)} disabled={Boolean(busy)} onChange={() => void toggleReward(reward)} /><span /></label>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'rules' && settings && (
        <form className="surface rules-form" onSubmit={saveSettings}>
          <div className="section-heading"><div><span className="section-icon section-icon--green"><SlidersHorizontal size={19} /></span><h2>Quy tắc riêng của {selectedChild.nickname}</h2></div></div>
          <div className="form-grid">
            <label>Ngân sách quà mỗi tháng (đ)<input type="number" min="0" step="10000" value={settings.monthlyBudgetVnd} onChange={(event) => setSettings({ ...settings, monthlyBudgetVnd: Number(event.target.value) })} /></label>
            <label>Giá tối đa mỗi món (đ)<input type="number" min="0" step="10000" value={settings.maxRewardPriceVnd} onChange={(event) => setSettings({ ...settings, maxRewardPriceVnd: Number(event.target.value) })} /></label>
            <label>Múi giờ<input value={settings.timezone} onChange={(event) => setSettings({ ...settings, timezone: event.target.value })} /></label>
            <label>Ngày tạo gợi ý hàng tuần<select value={settings.suggestionWeekday} onChange={(event) => setSettings({ ...settings, suggestionWeekday: Number(event.target.value) })}><option value={1}>Thứ Hai</option><option value={2}>Thứ Ba</option><option value={3}>Thứ Tư</option><option value={4}>Thứ Năm</option><option value={5}>Thứ Sáu</option><option value={6}>Thứ Bảy</option><option value={7}>Chủ Nhật</option></select></label>
            <label>Giờ tạo gợi ý<input type="time" value={settings.suggestionTime} onChange={(event) => setSettings({ ...settings, suggestionTime: event.target.value })} /></label>
          </div>
          <fieldset>
            <legend>Loại quà được phép cho {selectedChild.nickname}</legend>
            <div className="checkbox-grid">
              {categories.map(([value, label]) => (
                <label key={value} className="check-card">
                  <input
                    type="checkbox"
                    checked={settings.allowedRewardCategories.includes(value)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...settings.allowedRewardCategories, value]
                        : settings.allowedRewardCategories.filter((item) => item !== value);
                      if (next.length > 0) setSettings({ ...settings, allowedRewardCategories: next });
                    }}
                  />
                  <span><strong>{label}</strong></span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="rule-summary">
            <strong>Ngân sách còn lại của {selectedChild.nickname}:</strong> {number(settings.remainingBudgetVnd ?? settings.monthlyBudgetVnd)}đ
          </div>
          <button className="button button--primary button--wide" disabled={busy === 'settings'} type="submit"><Save size={18} /> {busy === 'settings' ? 'Đang lưu…' : 'Lưu quy tắc'}</button>

          <div className="profile-links">
            <div><span>👦</span><div><strong>Sở thích của {selectedChild.nickname}</strong><p>Cập nhật riêng để AI gợi ý chính xác cho bé.</p></div><Link className="button button--soft button--small" to="/survey">Khảo sát</Link></div>
            <div><span><Bell size={21} /></span><div><strong>Nhắc nhở & cài app</strong><p>Thông báo nhiệm vụ luôn ghi rõ hồ sơ bé tương ứng.</p></div><Link className="button button--soft button--small" to="/install">Mở</Link></div>
          </div>
        </form>
      )}
    </div>
  );
}
